import { NetworkStatus, WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect, useState } from 'react';

import {
  Project,
  TaskSortOption,
  useListTaskTagsQuery,
  Chat,
} from '@graphql/generated';
import { TaskTagType } from '@src/types/task';
/**
 * Retrieves a list of tasks
 */

const DEFAULT_PER_PAGE_COUNT = 10;

export const useListTaskTagsFromQuery = ({
  projectId = undefined,
  chatId = undefined,
  sortBy = undefined,
  first = DEFAULT_PER_PAGE_COUNT,
  last = undefined,
  after = undefined,
  before = undefined,
  fetchPolicy = 'cache-and-network',
  collaboratorsOnly = undefined,
  excludeCompleted = undefined,
  taskCollaborators = undefined,
  skip = false,
}: {
  projectId?: Project['id'];
  chatId?: Chat['id'];
  sortBy?: TaskSortOption;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
  collaboratorsOnly?: boolean;
  excludeCompleted?: boolean;
  taskCollaborators?: boolean;
}) => {
  const [refreshing, setRefreshing] = useState(false);

  const { data, loading, error, refetch, fetchMore, networkStatus } =
    useListTaskTagsQuery({
      variables: {
        projectIds: projectId && [projectId],
        chatId,
        sortBy,
        first,
        last,
        collaboratorsOnly,
        excludeCompleted,
        taskCollaborators,
        before,
        after,
      },
      fetchPolicy,
      notifyOnNetworkStatusChange: true,
      skip,
    });

  useEffect(() => {
    if (!loading) {
      setRefreshing(false);
    }
  }, [loading]);

  const { listTasks } = data || { listTasks: undefined };

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const fetchMoreFromCursor = () => {
    if (!tasks || !pageInfo?.hasNextPage || loading || isFetchingMore) return;

    const cursor = pageInfo.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
      },
    });
  };

  const { edges = [], pageInfo } = listTasks || { edges: [] };
  // TODO: This needs to be more properly typed to the returned graphql value.
  const tasks =
    (edges || []).reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as unknown as TaskTagType];
      } else {
        return acc;
      }
    }, [] as TaskTagType[]) || [];

  // pagination returns duplicate tasks when tasks are named the same resulting in duplicate key errors
  // can be removed when pagination is fixed
  const taskIds = tasks.map((t) => t.id);
  const filterOutDuplicates = tasks.filter((t, index) => {
    return !taskIds.includes(t.id.toString(), index + 1);
  });

  return {
    tasks: filterOutDuplicates,
    loading,
    isFetchingMore,
    error,
    pageInfo,
    refetch,
    fetchMore,
    fetchMoreFromCursor,
    refreshing,
    setRefreshing,
  };
};
